import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, History, Loader2, MessageCircle, Phone, UserCheck, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  BAPP_OPS,
  FilterBar,
  KordaBadge,
  KorwilBadge,
  Pagination,
  SectionTitle,
  SelectFilter,
  STATUS_OPS,
  StatusBadge,
  TIMELINE_STEPS,
  TableShell,
  Td,
  TextFilter,
  useDeferredFilters,
  SearchButton,
  Th,
  fmtDate,
  fmtDateTime,
  isLate,
} from "@/components/Shared";
import { cn } from "@/lib/utils";

const MATCH_LABEL = { MATCH: "MATCH", REPLACEMENT: "TEKNISI PENGGANTI", NOT_REGISTERED: "NOT REGISTERED" };
const FINANCE_BAPP_OPTIONS = ["SUDAH_SETOR", "BELUM_SETOR"];

function MatchBadge({ value, testId }) {
  if (!value) return <span className="text-xs text-slate-500">-</span>;
  return <StatusBadge value={MATCH_LABEL[value] || value} testId={testId} />;
}

function Timeline({ ops }) {
  const idx = TIMELINE_STEPS.findIndex((s) => s.key === ops?.status);
  return (
    <div className="space-y-0" data-testid="status-timeline">
      {TIMELINE_STEPS.map((s, i) => {
        const done = !!ops?.[s.ts] || (idx >= 0 && i <= idx);
        return (
          <div key={s.key} className="flex gap-3">
            <div className="flex flex-col items-center">
              <div
                className={cn(
                  "flex h-6 w-6 items-center justify-center rounded-full border text-[10px] font-bold",
                  done
                    ? "border-emerald-600 bg-emerald-600 text-white"
                    : "border-slate-700 bg-slate-900 text-slate-500"
                )}
              >
                {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </div>
              {i < TIMELINE_STEPS.length - 1 && (
                <div className={cn("w-0.5 flex-1", done ? "bg-emerald-700" : "bg-slate-800")} />
              )}
            </div>
            <div className="pb-4">
              <div
                data-testid={`timeline-${s.key.toLowerCase()}`}
                className={cn("text-sm font-medium", done ? "text-slate-100" : "text-slate-500")}
              >
                {s.label}
              </div>
              <div className="font-mono-data text-[11px] text-slate-500">
                {ops?.[s.ts] ? fmtDateTime(ops[s.ts]) : "belum"}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function UnitDetail({ id, onClose, onSaved }) {
  const [d, setD] = useState(null);
  const [ops, setOps] = useState({});
  const [aktual, setAktual] = useState({ aktual_nama: "", aktual_telepon: "", aktual_catatan: "" });
  const [techQ, setTechQ] = useState("");
  const [techs, setTechs] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/unit/${id}`).then(({ data }) => {
      setD(data);
      const o = data.unit.ops || {};
      setOps(o);
      setAktual({
        aktual_nama: o.aktual_nama || "",
        aktual_telepon: o.aktual_telepon || "",
        aktual_catatan: o.aktual_catatan || "",
      });
    });
  }, [id]);

  useEffect(load, [load]);

  useEffect(() => {
    if (techQ.length < 3) return setTechs([]);
    const t = setTimeout(() => {
      api
        .get("/unit/" + id + "/teknisi-search", { params: { q: techQ, per_page: 15 } })
        .then(({ data }) => setTechs(data.items || []))
        .catch(() => setTechs([]));
    }, 300);
    return () => clearTimeout(t);
  }, [techQ, d]);

  const patch = async (path, body, msg) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/unit/${id}/${path}`, body);
      setOps(data.ops);
      toast.success(msg);
      onSaved?.();
      load();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSaving(false);
    }
  };

  const saveAktual = (extra = {}) => {
    const payload = { ...aktual, ...extra };
    if (!payload.aktual_nama?.trim()) return toast.error("Nama teknisi aktual wajib diisi");
    if (!payload.aktual_telepon?.trim()) return toast.error("No. telepon teknisi aktual wajib diisi");
    patch("aktual", payload, "Teknisi aktual disimpan");
  };

  const whatsappUrl = (phone) => {
    const raw = String(phone || "").trim();
    if (!raw) return "";
    let number = raw.replace(/[^0-9]/g, "");
    if (number.startsWith("0")) number = "62" + number.slice(1);
    else if (!number.startsWith("62")) number = "62" + number;
    return number.length >= 10 ? `https://wa.me/${number}` : "";
  };

  const u = d?.unit;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/70" onClick={onClose}>
      <div
        data-testid="unit-detail-drawer"
        className="h-full w-full max-w-3xl overflow-y-auto border-l border-slate-800 bg-[#0d121c] p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-display text-lg font-bold text-white">{u?.sekolah || "Memuat..."}</h3>
            <div className="mt-1 flex items-center gap-2">
              <span className="font-mono-data text-xs text-blue-300">{id}</span>
              {u && <StatusBadge value={ops.status} testId="unit-status-badge" />}
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-400" />}
            </div>
          </div>
          <button data-testid="close-unit-detail" onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        {u && (
          <>
            <div className="mt-4 grid grid-cols-2 gap-2.5 text-xs sm:grid-cols-4">
              {[
                ["NPSN", u.npsn],
                ["Unit", u.unit],
                ["Provinsi", u.propinsi],
                ["Kabupaten/Kota", u.kabupaten_kota],
                ["Kecamatan", u.kecamatan || "-"],
                ["Korda", u.korda],
                ["Korwil", u.korwil],
                ["Jadwal Pusat", fmtDate(u.jadwal)],
                ["Alamat", u.alamat || "-"],
                ["PIC Sekolah", u.pic_sekolah || "-"],
                ["No HP PIC", u.pic_telepon || "-"],
                ["Status Pusat", u.status_pusat || "-"],
              ].map(([l, v]) => (
                <div key={l} className="rounded border border-slate-800 bg-slate-900/70 p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">{l}</div>
                  <div className="mt-0.5 truncate text-slate-200">{v}</div>
                </div>
              ))}
            </div>

            <h4 className="mt-6 mb-2 font-display text-base font-semibold text-white">
              Teknisi Pusat vs Teknisi Aktual
            </h4>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded border border-slate-800 bg-slate-900/70 p-3" data-testid="teknisi-pusat-box">
                <div className="mb-1 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
                  <UserCheck className="h-3.5 w-3.5" /> Teknisi Pusat (read only)
                </div>
                <div className="text-sm text-slate-100">
                  {u.teknisi_pusat_nama || <span className="text-slate-500">Tidak ada di data pusat</span>}
                </div>
                <div className="mt-0.5 flex items-center gap-1 font-mono-data text-xs text-slate-400">
                  <Phone className="h-3 w-3" /> {u.teknisi_pusat_telepon || "-"}
                </div>
                <div className="mt-1 font-mono-data text-[11px] text-blue-300">
                  {u.teknisi_pusat_kode || "-"}
                </div>
              </div>
              <div
                className={cn(
                  "rounded border p-3",
                  ops.match_status === "MATCH"
                    ? "border-emerald-800 bg-emerald-950/30"
                    : ops.match_status
                    ? "border-purple-800 bg-purple-950/25"
                    : "border-red-900/70 bg-red-950/25"
                )}
                data-testid="teknisi-aktual-box"
              >
                <div className="mb-1 flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-400">
                    <UserPlus className="h-3.5 w-3.5" /> Teknisi Aktual
                  </span>
                  <MatchBadge value={ops.match_status} testId="match-status-badge" />
                </div>
                <div className="text-sm text-slate-100" data-testid="unit-teknisi-aktual-name">
                  {ops.aktual_nama || <span className="text-red-300">Belum ada teknisi aktual</span>}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-2 font-mono-data text-xs text-slate-400">
                  <span className="flex items-center gap-1">
                    <Phone className="h-3 w-3" /> {ops.aktual_telepon || "-"}
                  </span>
                  {whatsappUrl(ops.aktual_telepon) && (
                    <a
                      href={whatsappUrl(ops.aktual_telepon)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 rounded-md border border-emerald-700/70 bg-emerald-950/40 px-2 py-1 text-[10px] font-semibold text-emerald-300 transition-colors hover:bg-emerald-900/60 hover:text-emerald-200"
                      title="Chat WhatsApp teknisi"
                    >
                      <MessageCircle className="h-3 w-3" />
                      WhatsApp
                    </a>
                  )}
                </div>
                {ops.aktual_catatan && (
                  <div className="mt-1 text-[11px] text-slate-400">{ops.aktual_catatan}</div>
                )}
              </div>
            </div>

            <div className="mt-3 rounded border border-slate-800 bg-slate-900/70 p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">
                Isi / ubah teknisi aktual — boleh teknisi pengganti di luar master
              </div>
              <input
                data-testid="unit-assign-search"
                value={techQ}
                onChange={(e) => setTechQ(e.target.value)}
                placeholder="Cari teknisi Korda & Kabupaten/Kota ini (min 3 karakter)..."
                className="mt-2 w-full rounded border border-slate-700 bg-slate-900 px-2.5 py-1.5 text-sm text-slate-200 outline-none focus:border-blue-500"
              />
              {techs.length > 0 && (
                <div className="mt-1 max-h-40 overflow-y-auto rounded border border-slate-700">
                  {techs.map((t) => (
                    <button
                      key={t.kode_teknisi}
                      data-testid={`assign-tech-${t.kode_teknisi}`}
                      onClick={() => {
                        setTechQ("");
                        setTechs([]);
                        setAktual({
                          aktual_nama: t.nama,
                          aktual_telepon: t.telepon || "",
                          aktual_catatan: aktual.aktual_catatan,
                        });
                        saveAktual({
                          aktual_kode: t.kode_teknisi,
                          aktual_nama: t.nama,
                          aktual_telepon: t.telepon || "",
                        });
                      }}
                      className="flex w-full items-center justify-between px-2.5 py-1.5 text-xs text-slate-300 transition-colors hover:bg-slate-800 hover:text-white"
                    >
                      <span>{t.nama}</span>
                      <span className="font-mono-data text-[10px] text-slate-500">
                        {t.telepon} · {t.kabupaten_kota}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                <TextFilter
                  label="Nama Teknisi Aktual"
                  testId="aktual-nama"
                  value={aktual.aktual_nama}
                  onChange={(v) => setAktual({ ...aktual, aktual_nama: v })}
                  placeholder="Nama (boleh di luar master)"
                />
                <TextFilter
                  label="No. Telepon"
                  testId="aktual-telepon"
                  value={aktual.aktual_telepon}
                  onChange={(v) => setAktual({ ...aktual, aktual_telepon: v })}
                  placeholder="628xxxxxxxxxx"
                />
                <TextFilter
                  label="Catatan"
                  testId="aktual-catatan"
                  value={aktual.aktual_catatan}
                  onChange={(v) => setAktual({ ...aktual, aktual_catatan: v })}
                  placeholder="Alasan penggantian"
                />
              </div>
              <button
                data-testid="btn-simpan-aktual"
                onClick={() => saveAktual({ aktual_kode: null })}
                disabled={!aktual.aktual_nama?.trim() || !aktual.aktual_telepon?.trim() || saving}
                className={cn(
                  "mt-3 rounded px-3 py-1.5 text-xs font-semibold transition-colors",
                  !aktual.aktual_nama?.trim() || !aktual.aktual_telepon?.trim() || saving
                    ? "cursor-not-allowed bg-slate-700 text-slate-500 opacity-70"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                )}
              >
                Simpan Teknisi Aktual
              </button>
            </div>

            <div className="mt-3 rounded border border-slate-800 bg-slate-900/70 p-3" data-testid="aktual-history">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-200">
                <History className="h-4 w-4 text-blue-300" />
                Riwayat Penggantian Teknisi Aktual
              </div>
              {Array.isArray(ops.aktual_history) && ops.aktual_history.length > 0 ? (
                <div className="space-y-3">
                  {[...ops.aktual_history].reverse().slice(0, 10).map((h, i) => (
                    <div key={`${h.at || "history"}-${i}`} className="rounded border border-slate-800 bg-slate-950/60 p-2.5">
                      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-400">
                        <span>{fmtDateTime(h.at)}</span>
                        <span className="max-w-[220px] truncate">{h.by || "-"}</span>
                      </div>
                      <div className="mt-2 grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500">Teknisi Sebelumnya</div>
                          <div className="mt-0.5 text-slate-300">{h.old_nama || "-"} · {h.old_telepon || "-"}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500">Teknisi Pengganti</div>
                          <div className="mt-0.5 font-semibold text-emerald-300">{h.new_nama || "-"} · {h.new_telepon || "-"}</div>
                        </div>
                      </div>
                      {h.alasan && (
                        <div className="mt-2 text-[11px] text-slate-400">Alasan: {h.alasan}</div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-slate-500">Belum ada riwayat penggantian teknisi aktual.</div>
              )}
            </div>

            <h4 className="mt-6 mb-2 font-display text-base font-semibold text-white">
              Status Instalasi
            </h4>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded border border-slate-800 bg-slate-900/70 p-3">
                <Timeline ops={ops} />
                {(ops.status_history || []).length > 0 && (
                  <div className="mt-3 border-t border-slate-800 pt-3" data-testid="status-history">
                    <div className="mb-1.5 text-[10px] uppercase tracking-wider text-slate-500">
                      Riwayat Status
                    </div>
                    <div className="space-y-1">
                      {[...ops.status_history].reverse().slice(0, 8).map((h, i) => (
                        <div key={i} className="flex items-center justify-between text-[11px]">
                          <span className="font-mono-data text-slate-400">{fmtDateTime(h.at)}</span>
                          <span className="text-slate-200">{(h.status || "").replace(/_/g, " ")}</span>
                          <span className="max-w-[120px] truncate text-slate-500">{h.by}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="rounded border border-slate-800 bg-slate-900/70 p-3">
                <div className="mb-2 text-[10px] uppercase tracking-wider text-slate-500">
                  Ubah status
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {STATUS_OPS.map((s) => (
                    <button
                      key={s}
                      data-testid={`btn-status-${s.toLowerCase()}`}
                      onClick={() => {
                        if (s === "TERKONFIRMASI") {
                          if (!ops.aktual_nama?.trim()) {
                            toast.error("Nama teknisi aktual wajib diisi sebelum konfirmasi jadwal");
                            return;
                          }
                          if (!ops.aktual_telepon?.trim()) {
                            toast.error("No. telepon teknisi aktual wajib diisi sebelum konfirmasi jadwal");
                            return;
                          }
                        }

                        if (s === "TERKENDALA" || s === "GAGAL_INSTALASI") {
                          const label = s === "TERKENDALA"
                            ? "Alasan / kendala instalasi:"
                            : "Alasan gagal instalasi:";

                          const catatan = window.prompt(label, ops.catatan || "");

                          if (catatan === null) return;

                          if (!catatan.trim()) {
                            toast.error("Alasan wajib diisi");
                            return;
                          }

                          patch(
                            "status",
                            { status: s, catatan: catatan.trim() },
                            `Status → ${s.replace(/_/g, " ")}`
                          );
                          return;
                        }

                        patch(
                          "status",
                          { status: s },
                          `Status → ${s.replace(/_/g, " ")}`
                        );
                      }}
                      className={cn(
                        "rounded border px-2 py-1 text-[10px] font-bold uppercase tracking-wide transition-colors",
                        ops.status === s
                          ? "border-blue-500 bg-blue-600 text-white"
                          : "border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white"
                      )}
                    >
                      {s.replace(/_/g, " ")}
                    </button>
                  ))}
                </div>
                <div className="mt-4 rounded border border-slate-800 bg-slate-950/60 p-3" data-testid="finance-bapp-status">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Status BAPP Finance</div>
                  <div className="mt-1 flex items-center gap-2">
                    <StatusBadge value={u?.bapp_status_finance || "BELUM_SETOR"} />
                    {u?.bapp_finance_setoran?.no_setoran && (
                      <span className="font-mono-data text-[10px] text-slate-500">{u.bapp_finance_setoran.no_setoran}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>


          </>
        )}
      </div>
    </div>
  );
}

export default function Unit() {
  const { korwilFilter } = useAuth();
  const [sp] = useSearchParams();
  const [opts, setOpts] = useState({});
  const { draft: f, applied: appliedF, set, apply, reset } = useDeferredFilters({ kabupaten: "", ops_status: "", bapp: "", q: "" });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [res, setRes] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ sudah_konfirmasi: 0, belum_konfirmasi: 0 });
  const [sel, setSel] = useState(null);
  const kategori = sp.get("kategori") || "";

  useEffect(() => {
    api.get("/filters/options").then(({ data }) => setOpts(data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { ...f, kategori: kategori || undefined, korwil: korwilFilter || undefined, page, per_page: perPage };
    Object.keys(params).forEach((k) => !params[k] && delete params[k]);
    api
      .get("/unit", { params })
      .then(({ data }) => setRes(data))
      .catch(() => setRes({ items: [], total: 0 }))
      .finally(() => setLoading(false));

    const summaryParams = { ...f, kategori: kategori || undefined, korwil: korwilFilter || undefined };
    Object.keys(summaryParams).forEach((k) => !summaryParams[k] && delete summaryParams[k]);
    api.get("/unit/summary", { params: summaryParams })
      .then(({ data }) => setSummary(data))
      .catch(() => setSummary({ sudah_konfirmasi: 0, belum_konfirmasi: 0 }));
  }, [appliedF, kategori, korwilFilter, page, perPage]);

  useEffect(load, [load]);

  const applyFilters = () => { setPage(1); apply(); };

  return (
    <div data-testid="page-unit">
      <SectionTitle
        title="Unit Instalasi"
        subtitle="1 id_transaksi = 1 unit instalasi. Klik baris untuk teknisi aktual, status instalasi & follow-up."
        right={
          <div className="flex flex-wrap items-center justify-end gap-3">
            <div className="rounded border border-emerald-700/50 bg-emerald-50 px-3 py-2 shadow-sm">
              <div className="text-[10px] uppercase tracking-wider text-emerald-700">SUDAH KONFIRMASI</div>
              <div data-testid="unit-summary-confirmed" className="font-mono-data text-lg font-semibold text-emerald-700">
                {(summary.sudah_konfirmasi || 0).toLocaleString("id-ID")}
              </div>
            </div>
            <div className="rounded border border-amber-700/50 bg-amber-50 px-3 py-2 shadow-sm">
              <div className="text-[10px] uppercase tracking-wider text-amber-700">BELUM KONFIRMASI</div>
              <div data-testid="unit-summary-unconfirmed" className="font-mono-data text-lg font-semibold text-amber-700">
                {(summary.belum_konfirmasi || 0).toLocaleString("id-ID")}
              </div>
            </div>
          </div>
        }
      />
      <FilterBar>
        <TextFilter label="Cari" testId="unit-search" value={f.q} onChange={set("q")} placeholder="id_transaksi / NPSN / sekolah / teknisi" />
        <SelectFilter label="Kabupaten/Kota" testId="unit-filter-kabupaten" value={f.kabupaten} onChange={set("kabupaten")} options={opts.kabupaten_unit} />
        <SelectFilter label="Status Instalasi" testId="unit-filter-status" value={f.ops_status} onChange={set("ops_status")} options={STATUS_OPS} />
        <SelectFilter label="Status BAPP" testId="unit-filter-bapp" value={f.bapp} onChange={set("bapp")} options={FINANCE_BAPP_OPTIONS} />
        <button
          data-testid="unit-reset"
          onClick={reset}
          className="rounded border border-slate-700 px-3 py-1.5 text-xs text-slate-300 transition-colors hover:bg-slate-800 hover:text-white"
        >
          Reset
        </button>
      
        <SearchButton onClick={applyFilters} testId="unit-search-button" /></FilterBar>

      <TableShell testId="unit-table" loading={loading} empty={res.items.length === 0}>
        <thead>
          <tr>
            <Th>NPSN ↑</Th>
            <Th>id_transaksi</Th>
            <Th>Sekolah</Th>
            <Th>Kabupaten</Th>
            <Th>Korda</Th>
            <Th>Korwil</Th>
            <Th>Jadwal</Th>
            <Th>Teknisi Pusat</Th>
            <Th>No HP Pusat</Th>
            <Th>Teknisi Aktual</Th>
            <Th>No HP Aktual</Th>
            <Th>Match</Th>
            <Th>Status</Th>
            <Th>BAPP</Th>
          </tr>
        </thead>
        <tbody>
          {res.items.map((u) => (
            <tr
              key={u.id_transaksi}
              data-testid={`unit-row-${u.id_transaksi}`}
              onClick={() => { setSel(u.id_transaksi); setTimeout(() => load(), 0); }}
              className="cursor-pointer border-b border-slate-800/70 transition-colors hover:bg-slate-800/50"
            >
              <Td className="font-mono-data text-xs text-blue-300">{u.npsn || "-"}</Td>
              <Td className="font-mono-data text-xs text-blue-300">{u.id_transaksi}</Td>
              <Td className="max-w-[180px] truncate text-slate-100">{u.sekolah}</Td>
              <Td className="text-xs">{u.kabupaten_kota}</Td>
              <Td><KordaBadge value={u.korda} /></Td>
              <Td><KorwilBadge value={u.korwil} /></Td>
              <Td className={`font-mono-data text-xs ${isLate(u) ? "text-red-300" : ""}`}>{fmtDate(u.jadwal)}</Td>
              <Td className="max-w-[130px] truncate text-xs">
                {u.teknisi_pusat_nama || <span className="text-slate-500">-</span>}
              </Td>
              <Td className="font-mono-data text-[11px] text-slate-400">
                {u.teknisi_pusat_telepon || "-"}
              </Td>
              <Td className="max-w-[130px] truncate text-xs">
                {u.ops?.aktual_nama || <span className="text-red-300">Belum ada</span>}
              </Td>
              <Td className="font-mono-data text-[11px] text-slate-400">
                {u.ops?.aktual_telepon || "-"}
              </Td>
              <Td><MatchBadge value={u.ops?.match_status} /></Td>
              <Td><StatusBadge value={u.ops?.status} /></Td>
              <Td><StatusBadge value={u.bapp_status_finance || "BELUM_SETOR"} /></Td>
            </tr>
          ))}
        </tbody>
      </TableShell>
      <Pagination page={res.page || page} perPage={perPage} total={res.total} onPage={setPage} onPerPage={(n) => { setPerPage(n); setPage(1); }} testId="unit-pagination" />
      {sel && <UnitDetail id={sel} onClose={() => setSel(null)} onSaved={load} />}
    </div>
  );
}
